import React, { useState, useEffect, useRef } from 'react';
import { RefreshCw, Shield } from 'lucide-react';

const OTPVerification = ({ onVerify, onResend, maskedEmail, expiresIn }) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [timeLeft, setTimeLeft] = useState(600); // 10 minutes in seconds
  const [canResend, setCanResend] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputRefs = useRef([]);

  // Initialize countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Handle resend cooldown
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [resendCooldown]);

  const handleInputChange = (index, value) => {
    if (value.length > 1) return;
    
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    
    // Clear error when user starts typing
    if (error) setError('');
    
    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
    
    // Auto-submit when all digits entered
    if (newOtp.every(digit => digit !== '') && newOtp.join('').length === 6) {
      handleSubmit(newOtp.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'Enter' && otp.join('').length === 6) {
      handleSubmit();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const digits = pastedData.replace(/\D/g, '').slice(0, 6);
    
    if (digits.length === 6) {
      const newOtp = digits.split('');
      setOtp(newOtp);
      inputRefs.current[5]?.focus();
      
      // Auto-submit pasted OTP
      setTimeout(() => handleSubmit(digits), 100);
    }
  };

  const handleSubmit = async (otpCode = otp.join('')) => {
    if (otpCode.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    setLoading(true);
    setError('');
    
    const result = await onVerify(otpCode);
    
    if (!result.success) {
      setError(result.error || 'Invalid verification code');
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    }
    
    setLoading(false);
  };

  const handleResend = async () => {
    if (!canResend) return;
    
    setLoading(true);
    setError('');
    setCanResend(false);
    
    const result = await onResend();
    
    if (result.success) {
      setTimeLeft(600); // Reset timer
      setResendCooldown(120); // 2 minutes cooldown
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } else {
      setError(result.error || 'Failed to resend code');
      setCanResend(true);
    }
    
    setLoading(false);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        {/* Logo and Title */}
        <div className="flex justify-center mb-6">
          <img
            src={`${process.env.PUBLIC_URL}/pkf-logo.svg`}
            alt="PKF Logo"
            className="h-12 w-auto"
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
        </div>
        
        <h2 className="text-center text-3xl font-extrabold text-gray-900 mb-2">
          Verify Your Email
        </h2>
        
        <p className="text-center text-sm text-gray-600 mb-2">
          We've sent a 6-digit verification code to
        </p>
        
        <p className="text-center text-sm font-medium text-gray-900 mb-6">
          {maskedEmail}
        </p>
      </div>

      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="space-y-6">
            {/* OTP Input Fields */}
            <div>
              <label className="block text-sm font-medium text-gray-700 text-center mb-4">
                Enter verification code
              </label>
              
              <div className="flex justify-center space-x-2 mb-4" onPaste={handlePaste}>
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={el => inputRefs.current[index] = el}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]"
                    maxLength="1"
                    value={digit}
                    onChange={(e) => handleInputChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    className="w-12 h-12 text-center text-lg font-bold border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                    disabled={loading}
                    autoComplete="off"
                  />
                ))}
              </div>
              
              <p className="text-xs text-gray-500 text-center">
                You can also paste the code from your email
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            {/* Timer and Status */}
            <div className="text-center">
              <div className="text-sm text-gray-600 mb-2">
                {timeLeft > 0 ? `Code expires in ${formatTime(timeLeft)}` : 'Code expired'}
              </div>
              
              {timeLeft === 0 && (
                <p className="text-sm text-red-600">
                  Your verification code has expired. Please request a new one.
                </p>
              )}
            </div>

            {/* Resend Button */}
            <div className="text-center">
              <button
                onClick={handleResend}
                disabled={!canResend || loading || resendCooldown > 0}
                className="inline-flex items-center text-sm text-primary-600 hover:text-primary-500 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                <RefreshCw className="h-4 w-4 mr-1" />
                {loading ? 'Sending...' : 
                 resendCooldown > 0 ? `Resend in ${formatTime(resendCooldown)}` : 
                 'Resend Code'}
              </button>
            </div>

            {/* Manual Submit Button */}
            <div>
              <button
                onClick={() => handleSubmit()}
                disabled={loading || otp.some(digit => digit === '') || timeLeft === 0}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                  <Shield className="h-5 w-5 text-primary-500 group-hover:text-primary-400" />
                </span>
                {loading ? 'Verifying...' : 'Verify Code'}
              </button>
            </div>
          </div>
        </div>
        
        {/* Help Text */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            Didn't receive the code? Check your spam folder or request a new one.
            <br />
            The code is valid for 10 minutes from when it was sent.
          </p>
        </div>
      </div>
    </div>
  );
};

export default OTPVerification; 